/**
 * aiApi — Marketing AI yordamchisi backend ulanishi.
 *
 * `AiContext.page`'ga qarab to'g'ri endpoint tanlanadi:
 *   - dashboard → POST /api/marketing/ai/summarize-dashboard { metrics }  → { data: { summary } }
 *   - campaign  → POST /api/marketing/ai/summarize-campaign { campaignId, payload } → { data: { summary } }
 *   - creative  → POST /api/marketing/ai/analyze-creative { creativeId } → { data: { analysis, scenarioScore, suggestions } }
 *   - generic/scenario yoki erkin chat → POST /api/marketing/ai/chat { messages, context } → { data: { reply } }
 *
 * Har bir funksiya assistant javob matnini qaytaradi. Xatolik yuqori
 * qatlamda (panel) tutiladi va o'zbekcha toast ko'rsatiladi.
 */
import { api } from "@/lib/axios";
import type { AiContext } from "./useAiAssistant";

/** Backend chat formatidagi xabar. */
export interface AiChatTurn {
  role: "user" | "assistant";
  content: string;
}

/** Har endpoint `{ data: T }` shaklida javob qaytaradi. */
interface ApiEnvelope<T> {
  data: T;
}

/** /chat — erkin chat javobi. */
interface ChatReply {
  reply: string;
}

/** /summarize-dashboard va /summarize-campaign — xulosa matni. */
interface SummaryReply {
  summary: string;
}

/** /chat-dashboard — joriy davr raqamlari bo'yicha savol-javob. */
interface ChatDashboardReply {
  answer: string;
}

/** /analyze-creative — tahlil matni + ball + tavsiyalar. */
interface CreativeAnalysisReply {
  analysis: string;
  scenarioScore: number;
  suggestions: string[];
}

/** Erkin chat — kontekst (page + data) bilan birga butun tarix yuboriladi. */
export async function sendChat(
  messages: AiChatTurn[],
  context: AiContext | null,
): Promise<string> {
  const res = await api.post<ApiEnvelope<ChatReply>>("/marketing/ai/chat", {
    messages,
    context: context
      ? { page: context.page, data: context.data }
      : { page: "generic" },
  });
  return res.data.data.reply;
}

/** Kreativ tahlili — creativeId orqali. Faqat tahlil matni qaytariladi. */
export async function analyzeCreative(creativeId: string): Promise<string> {
  const res = await api.post<ApiEnvelope<CreativeAnalysisReply>>(
    "/marketing/ai/analyze-creative",
    { creativeId },
  );
  return res.data.data.analysis;
}

/** Dashboard ko'rsatkichlari xulosasi — metrics payload. */
export async function summarizeDashboard(metrics: unknown): Promise<string> {
  const res = await api.post<ApiEnvelope<SummaryReply>>(
    "/marketing/ai/summarize-dashboard",
    { metrics },
  );
  return res.data.data.summary;
}

/** /chat-dashboard so'rov tanasi — joriy davr raqamlari kontekstida savol. */
export interface ChatDashboardBody {
  question: string;
  metrics: unknown;
  history?: AiChatTurn[];
}

/**
 * Dashboard chat — ko'rinib turgan davr raqamlari (metrics) + oxirgi tarix
 * bilan birga savol yuboriladi. AI markdown javob qaytaradi.
 */
export async function chatDashboard(
  body: ChatDashboardBody,
): Promise<string> {
  const res = await api.post<ApiEnvelope<ChatDashboardReply>>(
    "/marketing/ai/chat-dashboard",
    body,
  );
  return res.data.data.answer;
}

/** Kampaniya xulosasi — campaignId + ixtiyoriy payload. */
export async function summarizeCampaign(
  campaignId: string,
  payload?: unknown,
): Promise<string> {
  const res = await api.post<ApiEnvelope<SummaryReply>>(
    "/marketing/ai/summarize-campaign",
    { campaignId, payload },
  );
  return res.data.data.summary;
}

/**
 * Kontekstga qarab dastlabki (autoPrompt) so'rovni to'g'ri endpointga yo'naltiradi.
 * `userText` — foydalanuvchi yozgan matn (autoPrompt yoki erkin savol).
 * `history` — shu paytgacha bo'lgan chat turlari (chat endpoint uchun).
 */
export async function dispatchByContext(
  context: AiContext | null,
  userText: string,
  history: AiChatTurn[],
): Promise<string> {
  const page = context?.page ?? "generic";
  const data = context?.data as Record<string, unknown> | undefined;

  if (page === "creative") {
    const creativeId = typeof data?.creativeId === "string" ? data.creativeId : "";
    if (creativeId) return analyzeCreative(creativeId);
  }

  if (page === "dashboard") {
    return summarizeDashboard(data);
  }

  if (page === "campaign") {
    const campaignId = typeof data?.campaignId === "string" ? data.campaignId : "";
    if (campaignId) return summarizeCampaign(campaignId, data?.payload);
  }

  // generic / scenario / fallback — erkin chat (tarix + joriy matn)
  const messages: AiChatTurn[] = [
    ...history,
    { role: "user", content: userText },
  ];
  return sendChat(messages, context);
}
